import React from 'react';
import type { AttentionItem } from '../../domain/types';
import { BrandIcon, Button, Modal } from '../../ui/common';

type MergeStrategy = 'squash' | 'merge' | 'rebase';

const MERGE_STRATEGIES: Array<{ key: MergeStrategy; label: string; desc: string }> = [
  { key: 'squash', label: 'Squash and merge', desc: 'Combina todos los commits en un único commit en main' },
  { key: 'merge', label: 'Create a merge commit', desc: 'Conserva todos los commits individuales con un merge commit' },
  { key: 'rebase', label: 'Rebase and merge', desc: 'Aplica los commits linealmente sobre la rama base' },
];

interface GitHubCardMergeModalProps {
  item: AttentionItem;
  isOpen: boolean;
  mergeStrategy: MergeStrategy;
  commitMessage: string;
  onClose: () => void;
  onStrategyChange: (strategy: MergeStrategy) => void;
  onCommitMessageChange: (value: string) => void;
  onConfirm: () => void;
}

export const GitHubCardMergeModal: React.FC<GitHubCardMergeModalProps> = ({
  item,
  isOpen,
  mergeStrategy,
  commitMessage,
  onClose,
  onStrategyChange,
  onCommitMessageChange,
  onConfirm,
}) => (
  <Modal isOpen={isOpen} onClose={onClose} width={480}>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <BrandIcon source="github" size={18} />
          <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--z-text)' }}>
            Mergear Pull Request {item.primary}
          </span>
        </div>
        <button
          onClick={onClose}
          style={{ background: 'none', border: 'none', color: 'var(--z-dim)', cursor: 'pointer', fontSize: 16 }}
        >
          ✕
        </button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, background: 'var(--z-fill-2)' }}>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 700, color: 'var(--z-accent-3)' }}>
          {item.head}
        </span>
        <span style={{ color: 'var(--z-dim)', fontSize: 12 }}>→</span>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 700, color: 'var(--z-text-2)' }}>
          {item.base || 'main'}
        </span>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--z-faint)' }}>
          +{item.add} −{item.del}
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--z-text-2)' }}>
          Estrategia de fusión:
        </label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {MERGE_STRATEGIES.map(({ key, label, desc }) => (
            <label
              key={key}
              onClick={() => onStrategyChange(key)}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                padding: '8px 10px',
                borderRadius: 8,
                cursor: 'pointer',
                background: mergeStrategy === key ? 'var(--z-accent-wash)' : 'var(--z-fill)',
              }}
            >
              <input
                type="radio"
                name="mergeStrategy"
                checked={mergeStrategy === key}
                onChange={() => onStrategyChange(key)}
                style={{ marginTop: 2 }}
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--z-text)' }}>{label}</span>
                <span style={{ fontSize: 11, color: 'var(--z-dim)' }}>{desc}</span>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--z-text-2)' }}>
          Mensaje de commit:
        </label>
        <input
          type="text"
          value={commitMessage}
          onChange={(e) => onCommitMessageChange(e.target.value)}
          style={{
            padding: '8px 10px',
            borderRadius: 8,
            border: 'none',
            background: 'var(--z-fill)',
            color: 'var(--z-text)',
            fontFamily: 'inherit',
            fontSize: 12.5,
          }}
        />
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
        <Button variant="ghost" onClick={onClose}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={onConfirm}>
          Confirmar Merge
        </Button>
      </div>
    </div>
  </Modal>
);
